import { AfterViewInit, Component, TemplateRef, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { CampaignSegmentService } from 'src/app/private/services/campaign-segment.service';
import { TrackingService } from 'src/app/private/services/tracking.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
  selector: 'app-campaign-segments',
  templateUrl: './campaign-segments.component.html',
  styleUrls: ['./campaign-segments.component.scss']
})
export class CampaignSegmentsComponent implements AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  displayedColumns: string[] = ['segment_name', 'createdAt', 'action'];
  filterArray = [{ key: 'segment_name', value: 'Segment' }];
  totalRows = 0;
  currentPage = 0;
  pageSize = 25;
  pageSizeOptions: number[] = [10, 25, 100];
  initialLoad = true;
  reloading = false;
  btnLoading = false;
  formAction: 'Add' | 'Update' = 'Add';
  segmentName = '';
  segmentError = '';
  selectedSegment: any = null;

  constructor(
    private api: CampaignSegmentService,
    private localStorage: LocalStorageService,
    private modalService: NgbModal,
    private toast: ToastrService,
    private tracker: TrackingService
  ) {}

  ngAfterViewInit(): void {
    this.loadSegments();
  }

  loadSegments(): void {
    this.reloading = true;
    this.api.getAllCampaignSegmentTypes({
      client_id: this.localStorage.getClientId()
    }).subscribe({
      next: (res: any) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        this.applyRows(rows);
        this.initialLoad = false;
        this.reloading = false;
      },
      error: () => {
        this.applyRows([]);
        this.initialLoad = false;
        this.reloading = false;
        this.toast.error('Could not load campaign segments');
      }
    });
  }

  searchFilter(query: any): void {
    const value = typeof query === 'string'
      ? query
      : (query?.segment_name || '');
    this.dataSource.filter = String(value || '').trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
    this.totalRows = this.dataSource.filteredData.length;
  }

  openCreate(content: TemplateRef<any>): void {
    this.formAction = 'Add';
    this.segmentName = '';
    this.segmentError = '';
    this.selectedSegment = null;
    this.modalService.open(content, {
      size: 'lg',
      scrollable: true,
      centered: true
    });
  }

  openEdit(content: TemplateRef<any>, segment: any): void {
    this.formAction = 'Update';
    this.selectedSegment = segment;
    this.segmentName = segment?.segment_name || '';
    this.segmentError = '';
    this.modalService.open(content, {
      size: 'lg',
      scrollable: true,
      centered: true
    });
  }

  openDelete(content: TemplateRef<any>, segment: any): void {
    this.selectedSegment = segment;
    this.modalService.open(content, {
      size: 'md',
      scrollable: true,
      centered: true
    });
  }

  save(modal: any, submitted = true): void {
    const name = (this.segmentName || '').trim();
    if (!name) {
      if (submitted) {
        this.segmentError = 'Segment name is required';
      }
      return;
    }
    if (name.length > 50) {
      this.segmentError = 'Segment name can be up to 50 characters';
      return;
    }

    this.btnLoading = true;
    this.segmentError = '';
    const clientId = this.localStorage.getClientId();

    if (this.formAction === 'Add') {
      this.api.createCampaignSegmentType({ client_id: clientId, segment_name: name }).subscribe({
        next: (res: any) => {
          this.btnLoading = false;
          if (res?.already_exists) {
            this.toast.warning('This segment already exists');
          } else {
            this.toast.success('Segment created');
            this.tracker.track('Create', 'Campaign Segment', `Created campaign segment ${name}`);
          }
          modal.close();
          this.loadSegments();
        },
        error: () => {
          this.btnLoading = false;
          this.toast.error('Could not create segment');
        }
      });
      return;
    }

    this.api.updateCampaignSegmentType({
      id: this.selectedSegment.id,
      client_id: clientId,
      segment_name: name
    }).subscribe({
      next: () => {
        this.btnLoading = false;
        this.toast.success('Segment updated');
        this.tracker.track('Update', 'Campaign Segment', `Updated campaign segment ${name}`);
        modal.close();
        this.loadSegments();
      },
      error: () => {
        this.btnLoading = false;
        this.toast.error('Could not update segment');
      }
    });
  }

  confirmDelete(modal: any): void {
    if (!this.selectedSegment?.id) return;
    this.btnLoading = true;
    this.api.deleteCampaignSegmentType({
      id: this.selectedSegment.id,
      client_id: this.localStorage.getClientId()
    }).subscribe({
      next: () => {
        this.btnLoading = false;
        this.toast.success('Segment deleted');
        this.tracker.track('Delete', 'Campaign Segment', `Deleted campaign segment ${this.selectedSegment.segment_name}`);
        modal.close();
        this.loadSegments();
      },
      error: () => {
        this.btnLoading = false;
        this.toast.error('Could not delete segment');
      }
    });
  }

  private applyRows(rows: any[]): void {
    const currentFilter = this.dataSource.filter;
    this.dataSource = new MatTableDataSource<any>(rows);
    this.dataSource.filterPredicate = (row: any, filter: string) =>
      String(row?.segment_name || '').toLowerCase().includes(filter);
    this.dataSource.filter = currentFilter;
    this.dataSource.paginator = this.paginator;
    this.totalRows = this.dataSource.filteredData.length;
  }
}
